import React, { useEffect, useState } from 'react';
import { getCrmLeads, mergeCrmLeads } from '../config/api';

const CrmMergeLeadsDialog = ({ currentLead, onClose, onMerged }) => {
  const [query, setQuery] = useState('');
  const [leads, setLeads] = useState([]);
  const [otherLeadId, setOtherLeadId] = useState('');
  const [survivorLeadId, setSurvivorLeadId] = useState(String(currentLead.id));
  const [confirmed, setConfirmed] = useState(false);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const searchLeads = async (search = query) => {
    setSearching(true);
    setError('');
    try {
      const response = await getCrmLeads({ search: search.trim(), limit: 100 });
      const result = response?.data || response;
      const options = (result?.leads || []).filter(
        (lead) => String(lead.id) !== String(currentLead.id)
      );
      setLeads(options);
      setOtherLeadId((selected) =>
        options.some((lead) => String(lead.id) === selected) ? selected : ''
      );
    } catch (err) {
      setError(err.message || 'Could not load Leads.');
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    searchLeads('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLead.id]);

  const otherLead = leads.find((lead) => String(lead.id) === otherLeadId);
  const survivorIsCurrent = survivorLeadId === String(currentLead.id);
  const survivor = survivorIsCurrent ? currentLead : otherLead;
  const merged = survivorIsCurrent ? otherLead : currentLead;

  const submit = async (event) => {
    event.preventDefault();
    if (!otherLeadId || !otherLead) {
      setError('Select another Lead.');
      return;
    }
    if (!confirmed) {
      setError('Confirm the surviving Lead before merging.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const response = await mergeCrmLeads(currentLead.id, {
        otherLeadId: Number(otherLeadId),
        survivorLeadId: Number(survivorLeadId),
      });
      if (onMerged) onMerged(response?.data || response);
    } catch (err) {
      setError(err.message || 'Could not merge the Leads.');
    } finally {
      setSaving(false);
    }
  };

  const chooseOtherLead = (value) => {
    setOtherLeadId(value);
    setSurvivorLeadId(String(currentLead.id));
    setConfirmed(false);
    setError('');
  };

  return (
    <div className="modal-overlay" onClick={saving ? undefined : onClose}>
      <div className="modal-content" onClick={(event) => event.stopPropagation()}>
        <form onSubmit={submit}>
          <div className="modal-header">
            <h3>Merge Leads</h3>
            <button type="button" className="modal-close" onClick={onClose} disabled={saving}>×</button>
          </div>
          <div className="modal-body">
            <p className="crm-muted">
              Contacts, linked IDs, calls, comments, and tags will move to the surviving Lead.
              The other Lead will be archived. This action cannot be undone.
            </p>

            <div className="form-group">
              <label className="form-label" htmlFor="crm-merge-search">Find another Lead</label>
              <div className="crm-merge-search-row">
                <input
                  id="crm-merge-search"
                  className="form-control"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      searchLeads();
                    }
                  }}
                  placeholder="Search by Lead name or ID"
                  disabled={saving}
                />
                <button type="button" className="btn btn-secondary" onClick={() => searchLeads()} disabled={saving || searching}>
                  {searching ? 'Searching…' : 'Search'}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="crm-merge-other-lead">Lead to merge</label>
              <select
                id="crm-merge-other-lead"
                className="form-control"
                value={otherLeadId}
                onChange={(event) => chooseOtherLead(event.target.value)}
                disabled={saving || searching}
              >
                <option value="">Select a Lead</option>
                {leads.map((lead) => (
                  <option key={lead.id} value={lead.id}>
                    L-{String(lead.id).padStart(5, '0')} · {lead.displayName || `Lead #${lead.id}`}
                  </option>
                ))}
              </select>
            </div>

            {otherLead && (
              <fieldset className="crm-merge-survivor">
                <legend>Choose the surviving Lead</legend>
                <label>
                  <input
                    type="radio"
                    name="survivorLeadId"
                    value={currentLead.id}
                    checked={survivorIsCurrent}
                    onChange={(event) => {
                      setSurvivorLeadId(event.target.value);
                      setConfirmed(false);
                    }}
                    disabled={saving}
                  />
                  <span><strong>L-{String(currentLead.id).padStart(5, '0')}</strong> · {currentLead.displayName || `Lead #${currentLead.id}`}</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="survivorLeadId"
                    value={otherLead.id}
                    checked={!survivorIsCurrent}
                    onChange={(event) => {
                      setSurvivorLeadId(event.target.value);
                      setConfirmed(false);
                    }}
                    disabled={saving}
                  />
                  <span><strong>L-{String(otherLead.id).padStart(5, '0')}</strong> · {otherLead.displayName || `Lead #${otherLead.id}`}</span>
                </label>
              </fieldset>
            )}

            {survivor && merged && (
              <div className="crm-merge-confirmation">
                <strong>Survivor: Lead #{survivor.id}</strong>
                <span>Lead #{merged.id} will be merged and archived.</span>
                <label>
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(event) => {
                      setConfirmed(event.target.checked);
                      setError('');
                    }}
                    disabled={saving}
                  />
                  I confirm Lead #{survivor.id} is the surviving Lead.
                </label>
              </div>
            )}

            {error && <div className="crm-form-error">{error}</div>}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-danger" disabled={saving || !otherLead || !confirmed}>
              {saving ? 'Merging…' : 'Merge Leads'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CrmMergeLeadsDialog;
